<template>
  <div class="kpi-widget">
    <div class="kpi-content">
      <div class="kpi-header">
        <h4 class="kpi-title">{{ widget.title }}</h4>
        <v-icon :color="iconColor" size="20">{{ kpiIcon }}</v-icon>
      </div>
      
      <div class="kpi-metrics">
        <div class="primary-metric">
          <div class="metric-value" :style="{ color: primaryColor }">
            {{ formattedPrimaryValue }}
          </div>
          <div class="metric-change" :class="changeClass">
            <v-icon small>{{ changeIcon }}</v-icon>
            <span>{{ formattedChange }}</span>
          </div>
        </div>
        
        <div class="secondary-metrics">
          <div v-for="metric in secondaryMetrics" :key="metric.label" class="secondary-metric">
            <span class="metric-label">{{ metric.label }}</span>
            <span class="metric-value">{{ metric.value }}</span>
          </div>
        </div>
      </div>
      
      <!-- Mini progress bar -->
      <div v-if="progressValue" class="kpi-progress">
        <v-progress-linear
          :value="progressValue"
          :color="progressColor"
          height="4"
          rounded
        ></v-progress-linear>
        <div class="progress-label">
          <span>{{ progressLabel }}</span>
          <span>{{ progressValue }}%</span>
        </div>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="kpi-loading">
      <v-progress-circular
        indeterminate
        size="20"
        width="2"
        color="primary"
      ></v-progress-circular>
    </div>
    
    <!-- Error State -->
    <div v-if="error" class="kpi-error">
      <v-icon color="error" size="16">mdi-alert-circle</v-icon>
      <span class="text-caption">Error</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KpiWidget',
  props: {
    widget: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      primaryValue: null,
      change: null,
      secondaryMetrics: [],
      progressValue: null,
      progressLabel: '',
      loading: false,
      error: false
    }
  },
  computed: {
    kpiIcon() {
      const iconMap = {
        'kpi_performance': 'mdi-speedometer',
        'kpi_efficiency': 'mdi-chart-timeline-variant',
        'kpi_growth': 'mdi-trending-up',
        'default': 'mdi-chart-box'
      }
      return iconMap[this.widget.templateId] || iconMap.default
    },
    iconColor() {
      return (this.widget.config && this.widget.config.color && this.widget.config.color.default) || '#2196F3'
    },
    primaryColor() {
      return (this.widget.config && this.widget.config.color && this.widget.config.color.default) || '#1976D2'
    },
    formattedPrimaryValue() {
      if (this.primaryValue === null) return '--'
      
      if (this.widget.templateId === 'kpi_performance') {
        return `${this.primaryValue}%`
      }
      
      return new Intl.NumberFormat('en-US').format(this.primaryValue)
    },
    formattedChange() {
      if (this.change === null) return ''
      const sign = this.change > 0 ? '+' : ''
      return `${sign}${this.change}%`
    },
    changeClass() {
      if (this.change === null) return ''
      return this.change > 0 ? 'change-positive' : 'change-negative'
    },
    changeIcon() {
      if (this.change === null) return ''
      return this.change > 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'
    },
    progressColor() {
      if (this.progressValue >= 80) return 'success'
      if (this.progressValue >= 60) return 'warning'
      return 'error'
    }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.error = false
      
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 800))
        
        // Generate mock data
        const mockData = this.generateMockData()
        this.primaryValue = mockData.primaryValue
        this.change = mockData.change
        this.secondaryMetrics = mockData.secondaryMetrics
        this.progressValue = mockData.progressValue
        this.progressLabel = mockData.progressLabel
        
      } catch (error) {
        console.error('Error loading KPI data:', error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    generateMockData() {
      const mockDataMap = {
        'kpi_performance': {
          primaryValue: 87.5,
          change: 5.2,
          secondaryMetrics: [
            { label: 'Target', value: '90%' },
            { label: 'Last Month', value: '82.3%' }
          ],
          progressValue: 87.5,
          progressLabel: 'Goal Progress'
        },
        'kpi_efficiency': {
          primaryValue: 1247,
          change: -2.1,
          secondaryMetrics: [
            { label: 'Avg Time', value: '2.3h' },
            { label: 'Completed', value: '156' }
          ],
          progressValue: 73,
          progressLabel: 'Efficiency Score'
        },
        'kpi_growth': {
          primaryValue: 12543,
          change: 15.7,
          secondaryMetrics: [
            { label: 'Monthly', value: '+1.2K' },
            { label: 'Weekly', value: '+287' }
          ],
          progressValue: 92,
          progressLabel: 'Growth Target'
        },
        'default': {
          primaryValue: 100,
          change: 0,
          secondaryMetrics: [
            { label: 'Baseline', value: '100' },
            { label: 'Target', value: '120' }
          ],
          progressValue: 50,
          progressLabel: 'Progress'
        }
      }
      
      return mockDataMap[this.widget.templateId] || mockDataMap.default
    }
  }
}
</script>

<style scoped>
.kpi-widget {
  height: 100%;
  padding: 16px;
  position: relative;
}

.kpi-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.kpi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.kpi-title {
  font-size: 14px;
  font-weight: 600;
  color: #424242;
  margin: 0;
}

.kpi-metrics {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.primary-metric {
  text-align: center;
}

.primary-metric .metric-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}

.metric-change {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
}

.change-positive {
  color: #4CAF50;
}

.change-negative {
  color: #F44336;
}

.secondary-metrics {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.secondary-metric {
  flex: 1;
  text-align: center;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.secondary-metric .metric-label {
  display: block;
  font-size: 10px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.secondary-metric .metric-value {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #424242;
}

.kpi-progress {
  margin-top: auto;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  font-size: 10px;
  color: #666;
}

.kpi-loading,
.kpi-error {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

@media (max-width: 600px) {
  .kpi-widget {
    padding: 12px;
  }
  
  .primary-metric .metric-value {
    font-size: 24px;
  }
  
  .secondary-metrics {
    flex-direction: column;
    gap: 4px;
  }
}
</style> 